import { Suspense, lazy } from 'react';
import { Navigate, Outlet, useRoutes, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
// layouts
import DashboardLayout from '../layouts/dashboard';
import BlankDashboardLayout from '../layouts/dashboard/BlankDashboardLayout';
import AdminLayout from '../layouts/admin';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// components
import LoadingScreen from '../components/LoadingScreen';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import AdminGuard from '../guards/AdminGuard';
import OnboardingGuard from '../guards/OnboardingGuard';

function OnboardingAwareRedirect({ fallback }) {
  const { isOnboarded } = useSelector((state) => state.app);
  return <Navigate to={isOnboarded ? fallback : '/onboarding'} replace />;
}
OnboardingAwareRedirect.propTypes = { fallback: PropTypes.string.isRequired };

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes('/dashboard');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed'
            })
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      index: true,
      element: <OnboardingAwareRedirect fallback="/auth/login" />
    },
    {
      path: 'onboarding',
      element: (
        <OnboardingGuard>
          <Onboarding />
        </OnboardingGuard>
      )
    },
    // Auth routes
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          )
        },
        {
          path: 'reset-password',
          element: (
            <GuestGuard>
              <ResetPassword />
            </GuestGuard>
          )
        }
      ]
    },
    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <Outlet />
        </AuthGuard>
      ),
      children: [
        {
          element: <DashboardLayout />,
          children: [
            { index: true, element: <Home /> },
            { path: 'profile', element: <Profile /> },
            { path: 'progress', element: <Progress /> },
            { path: 'decks', element: <DeckLibrary /> },
            { path: 'decks/import', element: <ImportDeck /> },
            { path: 'decks/join/:inviteCode', element: <JoinDeck /> },
            {
              path: 'account',
              children: [
                { index: true, element: <Navigate to="/dashboard/account/me" replace /> },
                { path: 'me', element: <Settings /> },
              ]
            },
          ]
        },
        {
          element: <BlankDashboardLayout />,
          children: [
            { path: 'focus', element: <FocusTimer /> },
            { path: 'decks/:deckId', element: <DeckDetail /> },
            { path: 'decks/:deckId/review', element: <DeckReview /> },
            { path: 'decks/:deckId/unlock', element: <DeckUnlockPaywall /> },
          ]
        },
      ]
    },
    // Admin Routes
    {
      path: 'admin',
      element: (
        <AdminGuard>
          <AdminLayout />
        </AdminGuard>
      ),
      children: [
        { index: true, element: <Navigate to="/admin/users" replace /> },
        { path: 'users', element: <AdminUsers /> },
        { path: 'users/:uid', element: <AdminUserDetail /> },
        { path: 'audit', element: <AdminAudit /> },
      ],
    },
    // Subscription callback routes (Stripe redirects here — no auth guard)
    {
      path: 'subscriptions',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'success', element: <SubscriptionSuccess /> },
        { path: 'cancel', element: <SubscriptionCancel /> },
      ]
    },
    {
      path: '/',
      element: <Navigate to="/onboarding" replace />
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <OnboardingAwareRedirect fallback="/auth/login" /> }
      ]
    },
    { path: '*', element: <OnboardingAwareRedirect fallback="/404" /> }
  ]);
}

// IMPORT COMPONENTS

const Home = Loadable(lazy(() => import('../pages/dashboard/Home')));
const Profile = Loadable(lazy(() => import('../pages/dashboard/Profile')));
const Progress = Loadable(lazy(() => import('../pages/dashboard/Progress')));
const FocusTimer = Loadable(lazy(() => import('../pages/dashboard/FocusTimer')));
const Settings = Loadable(lazy(() => import('../pages/dashboard/Settings')));
const DeckLibrary = Loadable(lazy(() => import('../pages/dashboard/DeckLibrary')));
const ImportDeck = Loadable(lazy(() => import('../pages/dashboard/ImportDeck')));
const DeckDetail = Loadable(lazy(() => import('../pages/dashboard/DeckDetail')));
const DeckReview = Loadable(lazy(() => import('../pages/dashboard/DeckReview')));
const DeckUnlockPaywall = Loadable(lazy(() => import('../pages/dashboard/DeckUnlockPaywall')));
const JoinDeck = Loadable(lazy(() => import('../pages/dashboard/JoinDeck')));
// Admin
const AdminUsers = Loadable(lazy(() => import('../pages/admin/AdminUsers')));
const AdminUserDetail = Loadable(lazy(() => import('../pages/admin/AdminUserDetail')));
const AdminAudit = Loadable(lazy(() => import('../pages/admin/AdminAudit')));
// Subscription callbacks
const SubscriptionSuccess = Loadable(lazy(() => import('../pages/SubscriptionSuccess')));
const SubscriptionCancel = Loadable(lazy(() => import('../pages/SubscriptionCancel')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
// Auth
const Login = Loadable(lazy(() => import('../pages/authentication/Login')));
const Register = Loadable(lazy(() => import('../pages/authentication/Register')));
const ResetPassword= Loadable(lazy(() => import('../pages/authentication/ResetPassword')));
const Onboarding = Loadable(lazy(() => import('../pages/Onboarding')));